import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import api from '../../services/api';
import { metaTagService } from '../../services/metaTagService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Stack,
  FormControlLabel,
  Switch,
  Alert,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import MathEditor from './MathEditor';
import TagSelector from './TagSelector';
import TopicAutocomplete from '../common/TopicAutocomplete';
import BoardYearPairs from './BoardYearPairs';
import SubQuestionDialog from './SubQuestionDialog';
import { detailAnswerService } from '../../services/detailAnswerService';
import OptionalImageField from '../common/OptionalImageField';
import AnswerEvaluationFields from './AnswerEvaluationFields';

// Constants to avoid duplication
const QUESTION_TYPES = [
  { value: 'MULTIPLE', label: 'Multiple Choice' },
  { value: 'TRUE_FALSE', label: 'True/False' },
  { value: 'SHORT_ANSWER', label: 'Short Answer' }
];

const QuestionDialog = ({
  open,
  onClose,
  mode, // 'create' or 'edit'
  question,
  onSave,
  subjects,
  allTopics,
  boards,
  academicYears,
  curriculumAssociations = [],
  difficulties = [],
  examModes = [],
  onOpenDetailAnswer,
  onStateChange, // New prop to sync state with parent
  loading = false, // Loading state for save operation
  statuses = [], // Status options from parent
  isChampions = false, // Champions role specific behavior
  role = 'teacher', // User role: 'teacher', 'champion', 'admin'
  detailAnswerRefreshTrigger = 0, // Trigger to refresh detail answer check
}) => {
  const [editQuestion, setEditQuestion] = useState(() => ({
    ...question,
    has_subquestions: Boolean(question?.has_subquestions),
    sub_questions_data: question?.sub_questions || []
  }));
  const [newImage, setNewImage] = useState(null);
  const [newImagePreview, setNewImagePreview] = useState('');
  const [imageSource, setImageSource] = useState(null);
  const [teacherStatuses, setTeacherStatuses] = useState([]); // For teacher/champion specific statuses
  const [detailAnswerExists, setDetailAnswerExists] = useState(false); // Track if detail answer exists
  const [detailAnswerError, setDetailAnswerError] = useState(''); // Error message for detail answer validation
  const [subQuestionDialogOpen, setSubQuestionDialogOpen] = useState(false); // Control sub-question dialog
  const [subQuestionImageFiles, setSubQuestionImageFiles] = useState({}); // { index: File } for upload after save
  const subQuestionImageFilesRef = useRef({}); // Ref so handleSave always has latest (avoids stale closure)
  const subQuestionDetailImageFilesRef = useRef({}); // { index: File } detail answer images for upload after save
  const userSelectedNewImageRef = useRef(false); // True when user pasted/attached a new image (don't overwrite with question.image_url)
  const mainImageClearedRef = useRef(false); // True when user clicked Remove Image (so PATCH can send image_url: null)

  // Generic state update function to reduce code duplication
  const updateQuestionState = useCallback((field, value) => {
    setEditQuestion(prev => {
      // For tags, compare JSON strings
      if (field === 'tags') {
        if (JSON.stringify(prev[field]) === JSON.stringify(value)) {
          return prev;
        }
      } else if (prev[field] === value) {
        return prev;
      }
      
      const newState = { ...prev, [field]: value };
      if (onStateChange) {
        onStateChange(newState);
      }
      return newState;
    });
  }, [onStateChange]);

  // Specific handlers using the generic function
  const handleQuestionTextChange = useCallback((value) => updateQuestionState('question_text', value), [updateQuestionState]);
  const handleOptionsChange = useCallback((value) => updateQuestionState('options', value), [updateQuestionState]);
  const handleCorrectAnswerChange = useCallback((value) => updateQuestionState('correct_answer', value), [updateQuestionState]);
  const handleAnswerEvaluationChange = useCallback((value) => updateQuestionState('answer_evaluation', value), [updateQuestionState]);
  const handleTagsChange = useCallback((tags) => updateQuestionState('tags', tags), [updateQuestionState]);

  // Generic handler for select fields
  const handleSelectChange = useCallback((field, value) => {
    if (field === 'subject') {
      // Special case: clear topic when subject changes
      const newState = { ...editQuestion, subject: value, topic: '' };
      setEditQuestion(newState);
      if (onStateChange) {
        onStateChange(newState);
      }
    } else if (field === 'status') {
      // Special case: clear detail answer error when status changes
      const newState = { ...editQuestion, status: value };
      setEditQuestion(newState);
      if (onStateChange) {
        onStateChange(newState);
      }
      setDetailAnswerError('');
    } else {
      updateQuestionState(field, value);
    }
  }, [editQuestion, updateQuestionState, onStateChange]);

  // Handler for boolean fields (like is_active)
  const handleBooleanChange = useCallback((field, value) => {
    updateQuestionState(field, value);
  }, [updateQuestionState]);

  const handlePairsChange = useCallback((idx, field, value) => {
    setEditQuestion(prev => {
      let arr = [...prev.board_years];
      if (field === 'add') arr.push({ board: '', academic_year: '', difficulty: '' });
      else if (field === 'remove') arr = arr.filter((_, i) => i !== idx);
      else if (field === 'replace') arr = value; // Handle the new replace action
      else arr[idx] = { ...arr[idx], [field]: value };
      const newState = { ...prev, board_years: arr };
      // Sync with parent component
      if (onStateChange) {
        onStateChange(newState);
      }
      return newState;
    });
  }, [onStateChange]);

  // Topics filtered for the dialog based on selected subject
  const availableDialogTopics = useMemo(() => {
    if (!editQuestion.subject) return [];
    return allTopics.filter(t => {
      const subjField = t.subject ?? t.subject_id ?? (t.subject && t.subject.id);
      return String(subjField) === String(editQuestion.subject);
    });
  }, [editQuestion.subject, allTopics]);

  const topicsById = useMemo(() => {
    const map = new Map();
    (allTopics || []).forEach((topic) => {
      if (topic?.id != null) map.set(String(topic.id), topic);
    });
    return map;
  }, [allTopics]);

  const getChapterForPair = useCallback((pair) => {
    if (!editQuestion.subject || !editQuestion.topic || !pair?.board || !pair?.academic_year) return null;
    const assoc = (curriculumAssociations || []).find((item) => (
      item?.is_active !== false &&
      String(item.subject) === String(editQuestion.subject) &&
      String(item.topic) === String(editQuestion.topic) &&
      String(item.board) === String(pair.board) &&
      String(item.academic_year) === String(pair.academic_year) &&
      item.parent_mapping_topic != null
    ));
    if (!assoc) return null;
    const chapterTopic = topicsById.get(String(assoc.parent_mapping_topic));
    return {
      id: assoc.parent_mapping_topic,
      name: assoc.parent_mapping_topic_name || chapterTopic?.topic || chapterTopic?.name || '',
    };
  }, [curriculumAssociations, editQuestion.subject, editQuestion.topic, topicsById]);

  const isChapterValidationReady = Boolean(editQuestion.subject && editQuestion.topic);

  const boardYearHasCurriculumChapter = useCallback((pair) => (
    Boolean(pair?.board && pair?.academic_year && getChapterForPair(pair))
  ), [getChapterForPair]);
  
  // Track previous open state to detect when dialog opens/closes
  const prevOpenRef = useRef(open);
  const prevQuestionIdRef = useRef(question?.id);

  /** Convert API options (array or object) to comma-separated string for display (no brackets/quotes). parseOptions accepts this on save. */
  const optionsToDisplayValue = useCallback((opts) => {
    if (opts == null) return '';
    if (typeof opts === 'string') {
      const t = opts.trim();
      if (t.startsWith('[')) {
        try {
          const arr = JSON.parse(t);
          return Array.isArray(arr) ? arr.map((o) => (o != null ? String(o) : '')).join(', ') : t;
        } catch {
          return t;
        }
      }
      return t;
    }
    if (Array.isArray(opts)) {
      return opts.map((o) => (o != null ? String(o) : '')).join(', ');
    }
    if (typeof opts === 'object') {
      const keys = Object.keys(opts);
      const numeric = keys.every((k) => /^\d+$/.test(k));
      const ordered = numeric ? keys.sort((a, b) => Number(a) - Number(b)) : keys;
      const arr = ordered.map((k) => (opts[k] != null ? String(opts[k]) : ''));
      return arr.join(', ');
    }
    return '';
  }, []);

  // Sync local state with question prop when dialog opens/closes or question changes
  useEffect(() => {
    const dialogJustOpened = open && !prevOpenRef.current;
    const dialogJustClosed = !open && prevOpenRef.current;
    const questionChanged = question?.id !== prevQuestionIdRef.current;
    
    if (dialogJustOpened) {
      // Dialog just opened - reset state based on question prop
      if (question && question.id) {
        // Editing existing question – normalize options to string for the form (API returns array)
        setEditQuestion({
          ...question,
          has_subquestions: Boolean(question.has_subquestions),
          sub_questions_data: question.sub_questions || [],
          answer_evaluation: question.answer_evaluation || {},
          options: optionsToDisplayValue(question.options)
        });
        // Use image_proxy_url for display (same as DetailAnswerDialog – object storage via proxy)
        setNewImagePreview(question.image_proxy_url || question.image_url || '');
        setNewImage(null);
        setImageSource(null);
      } else {
        // Creating new question - reset to empty state
        setEditQuestion({
          question_text: '',
          subject: '',
          topic: '',
          board_years: [{ board: '', academic_year: '', difficulty: '' }],
          question_type: 'MULTIPLE',
          options: '',
          correct_answer: '',
          tags: [],
          exam_mode: '',
          answer_evaluation: {},
          status: null,
          is_active: true,
          has_subquestions: false,
          sub_questions_data: [],
          image_url: ''
        });
        setNewImagePreview('');
        setNewImage(null);
        setImageSource(null);
      }
    } else if (open && questionChanged && question?.id) {
      // Question changed while dialog is open (switching between questions)
      setEditQuestion({
        ...question,
        has_subquestions: Boolean(question.has_subquestions),
        sub_questions_data: question.sub_questions || [],
        answer_evaluation: question.answer_evaluation || {},
        options: optionsToDisplayValue(question.options)
      });
      setNewImagePreview(question.image_proxy_url || question.image_url || '');
      setNewImage(null);
      setImageSource(null);
    }
    
    if (open && question?.id && (question?.image_proxy_url || question?.image_url) && !dialogJustClosed && !userSelectedNewImageRef.current) {
      setNewImagePreview(question.image_proxy_url || question.image_url);
    }
    
    if (dialogJustClosed) {
      // Dialog just closed - reset everything
      setNewImage(null);
      setNewImagePreview('');
      setImageSource(null);
      setSubQuestionDialogOpen(false);
      setSubQuestionImageFiles({});
      subQuestionImageFilesRef.current = {};
      subQuestionDetailImageFilesRef.current = {};
      userSelectedNewImageRef.current = false;
      mainImageClearedRef.current = false;
    }
    if (dialogJustOpened || (open && questionChanged && question?.id)) {
      userSelectedNewImageRef.current = false;
      mainImageClearedRef.current = false;
    }
    
    // Update refs
    prevOpenRef.current = open;
    prevQuestionIdRef.current = question?.id;
  }, [open, question?.id, question?.image_proxy_url, question?.image_url, question?.options, optionsToDisplayValue]); // Re-run when question or its image URL updates (e.g. after fetch)
  

  
  // Automatically set options for TRUE_FALSE questions
  useEffect(() => {
    if (editQuestion.question_type === 'TRUE_FALSE') {
      setEditQuestion(prev => ({
        ...prev,
        options: '["True", "False"]'
      }));
    }
  }, [editQuestion.question_type]);

  // Fetch all statuses for teachers/champions (needed for published detection and filtered display)
  useEffect(() => {
    if ((isChampions || role === 'teacher') && open) {
      const fetchTeacherStatuses = async () => {
        try {
          const response = await api.get('/exams/statuses/');
          const allStatuses = response.data.results || response.data || [];
          
          // Store all statuses for published detection
          setTeacherStatuses(allStatuses);
        } catch (error) {
          console.warn('Failed to fetch teacher statuses:', error);
          setTeacherStatuses([]);
        }
      };
      
      fetchTeacherStatuses();
    }
  }, [isChampions, role, open]);

  // Check if detail answer exists for the current question
  useEffect(() => {
    const checkDetailAnswer = async () => {
      if (editQuestion.id && (isChampions || role === 'teacher')) {
        try {
          const detailAnswers = await detailAnswerService.getByQuestionId(editQuestion.id);
          // Check if detail answer exists AND has content (either text or image)
          const hasContent = detailAnswers && Array.isArray(detailAnswers) && detailAnswers.length > 0 && 
            detailAnswers.some(da => {
              // Check if detail answer has text content or image
              const hasText = da.answer && da.answer.trim().length > 0;
              const hasImage = da.answer_image || da.answer_image_proxy_url;
              return hasText || hasImage;
            });
          
          setDetailAnswerExists(hasContent);
          
          // Clear error when detail answer exists with content
          if (hasContent) {
            setDetailAnswerError('');
          }
        } catch (error) {
          console.warn('Failed to check detail answer:', error);
          setDetailAnswerExists(false);
        }
      } else {
        setDetailAnswerExists(false);
      }
    };
    
    checkDetailAnswer();
    
    // Also check when dialog opens/closes to refresh after detail answer is saved
    if (open) {
      // Add a small delay to ensure detail answer is saved before checking
      const timeoutId = setTimeout(checkDetailAnswer, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [editQuestion.id, isChampions, role, open, detailAnswerRefreshTrigger]);



  // Function to check overall validation status for real-time feedback
  const getValidationStatus = (question) => {
    const validation = validateQuestion(question);
    const errors = [...validation.errors];
    
    // Add detail answer validation only when trying to submit for review
    if ((isChampions || role === 'teacher') && question.status) {
      const selectedStatus = teacherStatuses.find(s => s.id === question.status);
      if (selectedStatus && selectedStatus.value === 'Under Review' && !detailAnswerExists) {
        errors.push('Detail answer is required before submitting for review');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errorCount: errors.length,
      hasErrors: errors.length > 0,
      errors: errors
    };
  };

  // Comprehensive validation function for both create and edit modes
  const validateQuestion = (q) => {
    const errors = [];
    
    // Required field validations
    if (!(typeof q.question_text === 'string' && q.question_text.trim())) {
      errors.push('Question text is required');
    }
    
    // Difficulty validation is now handled at the board-year pair level
    
    if (!q.question_type) {
      errors.push('Question type is required');
    }
    
    if (!q.subject) {
      errors.push('Subject is required');
    }
    
    if (!q.topic) {
      errors.push('Topic is required');
    }
    
    // Correct answer is optional for the main question.
    
    // If has sub-questions, validate sub-questions
    if (q.has_subquestions) {
      if (!q.sub_questions_data || q.sub_questions_data.length === 0) {
        errors.push('At least one sub-question is required when "Have sub questions" is checked');
      } else {
        q.sub_questions_data.forEach((sq, index) => {
          if (!sq.question_text || !sq.question_text.trim()) {
            errors.push(`Sub-question ${String.fromCharCode(97 + index)}: Question text is required`);
          }
          if (sq.question_type === 'MULTIPLE') {
            const opts = parseOptions(sq.options);
            if (!Array.isArray(opts) || opts.length === 0) {
              errors.push(`Sub-question ${String.fromCharCode(97 + index)}: Options are required for Multiple Choice`);
            } else if (sq.correct_answer && !opts.map((o) => String(o).trim()).includes(String(sq.correct_answer).trim())) {
              errors.push(`Sub-question ${String.fromCharCode(97 + index)}: Correct answer must be one of the options`);
            }
          }
        });
      }
    }
    
    // Validation for regular questions (when has_subquestions is false)
    if (!q.has_subquestions) {
      // Options and correct answer validation for specific question types
      if (q.question_type === 'MULTIPLE') {
        const opts = parseOptions(q.options);
        if (!Array.isArray(opts) || opts.length === 0) {
          errors.push('Options must be a non-empty array for Multiple Choice questions');
        } else if (q.correct_answer && !opts.map((o) => String(o).trim()).includes(String(q.correct_answer).trim())) {
          errors.push('Correct answer must be one of the options');
        }
      }
      
      // For TRUE_FALSE questions, if provided, correct answer must be "True" or "False"
      if (q.question_type === 'TRUE_FALSE' && q.correct_answer && !['True', 'False'].includes(q.correct_answer)) {
        errors.push('Correct answer must be either "True" or "False"');
      }
    }
    
    // Board-year pair validation
    if (!q.board_years || q.board_years.length === 0) {
      errors.push('At least one Board-Year pair is required');
    } else {
      const invalidPair = q.board_years.some(p => !p.board || !p.academic_year || !p.difficulty);
      if (invalidPair) {
        errors.push('Each Board-Year pair must have Board, Year, and Difficulty selected');
      }
      if (q.subject && q.topic) {
        const pairNotInCurriculum = q.board_years.some(p => p.board && p.academic_year && !boardYearHasCurriculumChapter(p));
        if (pairNotInCurriculum) {
          errors.push('Selected topic is not part of the curriculum for one or more selected board/year rows');
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  };

  const parseOptions = (options) => {
    if (options == null) return [];
    if (Array.isArray(options)) {
      return options.map((o) => (o == null ? '' : String(o)));
    }
    const text = String(options);
    const trimmed = text.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed.map((o)=>String(o)) : [];
    } catch {
      return trimmed.split(',').map(s => s.trim()).filter(Boolean);
    }
  };

  const handleSave = async () => {
    try {
      const validation = validateQuestion(editQuestion);
      if (!validation.isValid) {
        // Don't show alert - validation status is already displayed in the UI
        return;
      }

      // Check if trying to submit for review without detail answer (for teachers and champions)
      if ((isChampions || role === 'teacher') && editQuestion.status) {
        // Find the status object to check if it's "Submit for Review"
        const selectedStatus = teacherStatuses.find(s => s.id === editQuestion.status);
        if (selectedStatus && selectedStatus.value === 'Under Review' && !detailAnswerExists) {
          setDetailAnswerError('Please add working steps in the detail answer section beside the correct answer before submitting for review.');
          return;
        }
      }

      if (!onSave || typeof onSave !== 'function') {
        console.error('onSave is not a function:', onSave);
        return;
      }
    
    // Prepare question data for saving
    const questionData = { ...editQuestion };
    
    // If has_subquestions is true, clear main question's correct_answer
    if (questionData.has_subquestions) {
      questionData.correct_answer = '';
    }
    
    // Process sub-questions data: ensure order is set and parse options
    if (questionData.has_subquestions && questionData.sub_questions_data) {
      questionData.sub_questions_data = questionData.sub_questions_data.map((sq, index) => ({
        ...sq,
        order: index + 1,
        options: sq.question_type === 'MULTIPLE' ? parseOptions(sq.options) : []
      }));
    } else {
      questionData.sub_questions_data = [];
    }
    
    // Process tags: create new tags and get their IDs
    let processedTags = [];
    const rawTags = editQuestion.tags || [];
    
    if (Array.isArray(rawTags) && rawTags.length > 0) {
      // Separate new tags (objects with _isNew) from existing tags (IDs or objects with id)
      const newTags = [];
      const existingTagIds = [];
      const tagNames = [];
      
      for (const tag of rawTags) {
        // Skip null/undefined
        if (!tag) continue;
        
        // If it's a number, it's an existing tag ID
        if (typeof tag === 'number') {
          existingTagIds.push(tag);
        }
        // If it's a string, resolve numeric strings or existing tag names
        else if (typeof tag === 'string') {
          const trimmed = tag.trim();
          if (/^\d+$/.test(trimmed)) {
            existingTagIds.push(Number(trimmed));
          } else if (trimmed) {
            tagNames.push(trimmed);
          }
        }
        // If it's an object
        else if (typeof tag === 'object') {
          // Check if it's a new tag (has _isNew flag)
          if (tag._isNew === true) {
            newTags.push(tag);
          }
          // If it has an id, it's an existing tag
          else if (tag.id && typeof tag.id === 'number') {
            existingTagIds.push(tag.id);
          }
          else if (tag.name && typeof tag.name === 'string') {
            tagNames.push(tag.name.trim());
          }
          // Otherwise, log warning
          else {
            console.warn('Unexpected tag format:', tag);
          }
        }
      }

      const resolveExistingTagIdsByName = async (names) => {
        if (!names.length) return [];
        try {
          metaTagService.clearCache();
          const allTags = await metaTagService.getActiveTags();
          const list = Array.isArray(allTags) ? allTags : (allTags?.results || []);
          return names
            .map((name) => list.find((t) => String(t.name).trim().toLowerCase() === String(name).trim().toLowerCase())?.id)
            .filter((id) => typeof id === 'number' && id > 0);
        } catch (error) {
          console.error('Failed to resolve existing tag names:', error);
          return [];
        }
      };

      const resolvedNameIds = await resolveExistingTagIdsByName(tagNames);

      // Create new tags via API
      if (newTags.length > 0) {
        try {
          const createdTagIds = [];
          for (const newTag of newTags) {
            if (!newTag.name || typeof newTag.name !== 'string') {
              console.warn('Invalid new tag object:', newTag);
              continue;
            }
            
            try {
              const createdTag = await metaTagService.create({
                name: newTag.name.trim(),
                color: newTag.color || '#1976d2',
                is_active: true
              });
              if (createdTag && createdTag.id) {
                createdTagIds.push(createdTag.id);
              }
            } catch (error) {
              console.error('Failed to create tag:', newTag.name, error);
              const existingIds = await resolveExistingTagIdsByName([newTag.name]);
              createdTagIds.push(...existingIds);
            }
          }
          // Clear cache so TagSelector can refresh with new tags
          if (createdTagIds.length > 0) {
            metaTagService.clearCache();
          }
          // Combine existing tag IDs with newly created tag IDs
          processedTags = [...existingTagIds, ...resolvedNameIds, ...createdTagIds];
        } catch (error) {
          console.error('Error processing tags:', error);
          // Fallback to existing tags only if creation fails
          processedTags = [...existingTagIds, ...resolvedNameIds];
        }
      } else {
        // No new tags, just use existing tag IDs
        processedTags = [...existingTagIds, ...resolvedNameIds];
      }
    }
    
    // Ensure processedTags is an array of numbers only
    processedTags = [...new Set(processedTags)].filter(id => typeof id === 'number' && !isNaN(id));
    
    // Ensure status is properly set - handle empty string case for "No Status"
    // Final validation: ensure tags is an array of numbers only
    const finalTags = Array.isArray(processedTags) 
      ? processedTags.filter(id => typeof id === 'number' && !isNaN(id) && id > 0)
      : [];
    
    // Create a clean copy of editQuestion without tags, then add processed tags
    const { tags: _, ...questionWithoutTags } = editQuestion;
    
    // Process sub-questions data: ensure order is set and parse options
    let subQuestionsData = [];
    if (questionWithoutTags.has_subquestions && questionWithoutTags.sub_questions_data) {
      subQuestionsData = questionWithoutTags.sub_questions_data.map((sq, index) => {
        // Remove image_url if it's a blob URL (will be handled separately after question creation)
        const processedSq = {
          question_text: sq.question_text || '',
          question_type: sq.question_type || 'MULTIPLE',
          options: sq.question_type === 'MULTIPLE' ? parseOptions(sq.options) : [],
          correct_answer: sq.correct_answer || '',
          detail_answer: sq.detail_answer || '',
          answer_evaluation: sq.answer_evaluation || {},
          marks: sq.marks || 1,
          order: index + 1,
          is_active: sq.is_active !== undefined ? sq.is_active : true
        };
        // Only include image_url if it's a stored URL (OCI or media); exclude blob, proxy, and relative URLs
        const url = sq.image_url && typeof sq.image_url === 'string' ? sq.image_url.trim() : '';
        if (url &&
            (url.startsWith('http://') || url.startsWith('https://')) &&
            !url.includes('/image-proxy/') &&
            !url.includes('/image-proxy?') &&
            !url.startsWith('blob:')) {
          processedSq.image_url = url;
        }
        // Same rule for the detail answer image (backend recreates sub-questions on update)
        const detailUrl = sq.detail_answer_image && typeof sq.detail_answer_image === 'string' ? sq.detail_answer_image.trim() : '';
        if (detailUrl &&
            (detailUrl.startsWith('http://') || detailUrl.startsWith('https://')) &&
            !detailUrl.includes('/image-proxy/') &&
            !detailUrl.includes('/image-proxy?') &&
            !detailUrl.startsWith('blob:')) {
          processedSq.detail_answer_image = detailUrl;
        }
        return processedSq;
      });
    }
    
    // If has_subquestions is true, clear main question's correct_answer
    const correctAnswer = questionWithoutTags.has_subquestions ? '' : questionWithoutTags.correct_answer;
    
    const questionToSave = {
      ...questionWithoutTags,
      tags: finalTags,
      status: editQuestion.status === '' ? null : editQuestion.status,
      correct_answer: correctAnswer,
      sub_questions_data: subQuestionsData
    };
    if (mainImageClearedRef.current && !newImage) {
      questionToSave.image_url = null;
    }
    
    // Debug log to verify tags format before sending
    console.log('Question to save - tags:', finalTags, 'type check:', finalTags.every(id => typeof id === 'number'));
    console.log('Question to save - full object tags field:', questionToSave.tags);
    console.log('Question to save - sub_questions_data:', questionToSave.sub_questions_data);
    
    // Final safety check: ensure no tag objects slipped through
    if (questionToSave.tags.some(tag => typeof tag !== 'number')) {
      console.error('ERROR: Non-numeric tags detected!', questionToSave.tags);
      const sanitizedTags = questionToSave.tags
        .map(tag => {
          if (typeof tag === 'number') return tag;
          if (tag && typeof tag === 'object' && tag.id && typeof tag.id === 'number') return tag.id;
          return null;
        })
        .filter(id => id !== null && typeof id === 'number');
      questionToSave.tags = sanitizedTags;
      console.log('Sanitized tags:', sanitizedTags);
    }
    
    // Ensure sub_questions_data is properly formatted
    if (questionToSave.has_subquestions) {
      if (!questionToSave.sub_questions_data || questionToSave.sub_questions_data.length === 0) {
        console.error('ERROR: has_subquestions is true but no sub_questions_data provided');
        console.error('editQuestion:', editQuestion);
        console.error('questionToSave:', questionToSave);
        return;
      }
      // Validate each sub-question has required fields
      for (let i = 0; i < questionToSave.sub_questions_data.length; i++) {
        const sq = questionToSave.sub_questions_data[i];
        if (!sq.question_text || !sq.question_text.trim()) {
          console.error(`ERROR: Sub-question ${i + 1} missing question_text`, sq);
          return;
        }
        // Validate options for MULTIPLE choice sub-questions
        if (sq.question_type === 'MULTIPLE') {
          const opts = parseOptions(sq.options);
          if (!Array.isArray(opts) || opts.length === 0) {
            console.error(`ERROR: Sub-question ${i + 1} (MULTIPLE) missing options`, sq);
            return;
          }
        }
      }
      console.log('Sub-questions validation passed:', questionToSave.sub_questions_data);
    }
    
    // Call onSave with question data, main-question image file, and sub-question image files (use ref for latest)
    const subQuestionFiles = subQuestionImageFilesRef.current || subQuestionImageFiles;
    const subQuestionDetailFiles = subQuestionDetailImageFilesRef.current || {};
    console.log('Calling onSave with questionToSave:', questionToSave, 'imageFile:', newImage, 'subQuestionImageFiles:', subQuestionFiles);
    try {
      const result = onSave(questionToSave, newImage, subQuestionFiles, subQuestionDetailFiles);
      // If onSave returns a promise, await it
      if (result && typeof result.then === 'function') {
        console.log('onSave returned a promise, awaiting...');
        await result;
        console.log('onSave promise resolved');
      } else {
        console.log('onSave completed synchronously');
      }
    } catch (error) {
      console.error('Error in handleSave calling onSave:', error);
      // Re-throw to let parent handle error display
      throw error;
    }
  } catch (error) {
    console.error('Error in handleSave outer catch:', error);
    // Don't close dialog on error - let parent handle error display
    throw error; // Re-throw to let parent handle
  }
};

  const handleClearImage = () => {
    userSelectedNewImageRef.current = true;
    mainImageClearedRef.current = true;
    setNewImage(null);
    setNewImagePreview('');
    setImageSource(null);
    setEditQuestion((prev) => {
      const newState = { ...prev, image_url: '', image_proxy_url: '' };
      if (onStateChange) onStateChange(newState);
      return newState;
    });
  };

  const handleClose = () => {
    // Reset all state to initial values
    if (question) {
      // If editing, reset to original question
      setEditQuestion({
        ...question,
        has_subquestions: Boolean(question.has_subquestions),
        sub_questions_data: question.sub_questions || [],
        answer_evaluation: question.answer_evaluation || {}
      });
      setNewImagePreview(question.image_proxy_url || question.image_url || '');
    } else {
      // If creating new, reset to empty state
      setEditQuestion({
        question_text: '',
        subject: '',
        topic: '',
        board_years: [{ board: '', academic_year: '', difficulty: '' }],
        question_type: 'MULTIPLE',
        options: '',
        correct_answer: '',
        answer_evaluation: {},
        tags: [],
        exam_mode: '',
        status: null,
        is_active: true,
        has_subquestions: false,
        sub_questions_data: [],
        image_url: ''
      });
      setNewImagePreview('');
    }
    setNewImage(null);
    setImageSource(null);
    setTeacherStatuses([]); // Reset teacher statuses
    setDetailAnswerExists(false); // Reset detail answer state
    setDetailAnswerError(''); // Clear any error messages
    setSubQuestionDialogOpen(false); // Close sub-question dialog if open
    onClose();
  };

  const status = getValidationStatus(editQuestion);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      transitionDuration={0}
      TransitionProps={{ timeout: { enter: 0, exit: 0 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'create' ? 'Add New Question' : `Edit Question #${editQuestion.id}`}
        <Button onClick={handleClose} sx={{ ml: 'auto' }} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Fields marked with * are required
        </Typography>
        
        {/* Validation Status */}
        {(() => {
          return (
            <Box sx={{ 
              mb: 2, 
              p: 2, 
              backgroundColor: status.isValid ? 'success.light' : 'warning.light', 
              borderRadius: 1,
              border: `1px solid ${status.isValid ? 'success.main' : 'warning.main'}`
            }}>
              <Typography variant="caption" color={status.isValid ? 'success.contrastText' : 'warning.contrastText'} sx={{ display: 'block', mb: 1, fontWeight: 'bold' }}>
                {status.isValid ? '✅ All validations passed' : `⚠️ ${status.errorCount} validation error${status.errorCount > 1 ? 's' : ''} found`}
              </Typography>
              {!status.isValid && (
                <>
                  <Typography variant="caption" color="warning.contrastText" sx={{ display: 'block', mb: 1 }}>
                    Please fix the errors below before saving:
                  </Typography>
                  {status.errors.map((error, index) => (
                    <Typography key={index} variant="caption" color="warning.contrastText" sx={{ display: 'block', ml: 1 }}>
                      • {error}
                    </Typography>
                  ))}
                </>
              )}
            </Box>
          );
        })()}

        <Stack spacing={2} mt={1}>
          <Typography variant="subtitle2">Board & Academic Year *</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            At least one board-year-difficulty pair with all fields selected is required
          </Typography>
          <BoardYearPairs 
            pairs={editQuestion.board_years} 
            boards={boards} 
            years={academicYears} 
            difficulties={difficulties}
            onChange={handlePairsChange} 
            getChapterForPair={getChapterForPair}
            requireChapter
            chapterValidationReady={isChapterValidationReady}
            chapterDisabledReason="Select subject and topic to resolve chapter"
          />
          {(!editQuestion.board_years || editQuestion.board_years.length === 0) && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              ⚠️ At least one board-year pair is required
            </Typography>
          )}
          {editQuestion.board_years && editQuestion.board_years.length > 0 && editQuestion.board_years.some(p => !p.board || !p.academic_year || !p.difficulty) && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              ⚠️ Each board-year pair must have Board, Year, and Difficulty selected
            </Typography>
          )}
          {isChapterValidationReady && editQuestion.board_years && editQuestion.board_years.some(p => p.board && p.academic_year && !boardYearHasCurriculumChapter(p)) && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              Selected topic is not part of the curriculum for one or more selected board/year rows
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Subject and topic are required for proper categorization
          </Typography>
          <TextField 
            select 
            label="Subject *" 
            value={editQuestion.subject} 
            onChange={(e) => handleSelectChange('subject', e.target.value)} 
            fullWidth 
            error={!editQuestion.subject}
          >
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
            
          <TopicAutocomplete
            options={availableDialogTopics}
            value={editQuestion.topic}
            onChange={(id) => handleSelectChange('topic', id)}
            disabled={!editQuestion.subject}
            error={!editQuestion.topic}
            placeholder={editQuestion.subject ? 'Type to search topics...' : 'Select a subject first'}
            label="Topic *"
          />
          
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Question text is required and cannot be blank
          </Typography>
          <MathEditor
            value={editQuestion.question_text}
            onChange={handleQuestionTextChange}
            label="Question Text *"
            placeholder="Enter your question text here..."
            helperText="Question text is required. Use the math button (∑) to insert mathematical formulas."
            error={!(typeof editQuestion.question_text === 'string' && editQuestion.question_text.trim())}
          />
          
          {/* Image Upload Section - same component as detail answer */}
          <OptionalImageField
            label="Image (optional)"
            previewUrl={newImagePreview || editQuestion.image_proxy_url || editQuestion.image_url || ''}
            onClear={handleClearImage}
            onFileSelect={(file) => {
              userSelectedNewImageRef.current = true;
              mainImageClearedRef.current = false;
              setNewImage(file);
              setNewImagePreview(URL.createObjectURL(file));
              setImageSource('file');
            }}
            onPaste={(file) => {
              userSelectedNewImageRef.current = true;
              mainImageClearedRef.current = false;
              setNewImage(file);
              setNewImagePreview(URL.createObjectURL(file));
              setImageSource('paste');
            }}
            disabled={imageSource === 'paste'}
            inputId="main-question-image-file"
            emptyInstruction="Click or paste screenshot (Ctrl+V) here to attach image"
            imageAlt="Question"
          />
          
          {/* Have Sub-Questions Toggle - Moved immediately after image upload */}
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(editQuestion.has_subquestions)}
                onChange={(e) => {
                  const hasSubQuestions = e.target.checked;
                  setEditQuestion(prev => {
                    const newState = {
                      ...prev,
                      has_subquestions: hasSubQuestions,
                      correct_answer: hasSubQuestions ? '' : prev.correct_answer,
                      // Always preserve sub_questions_data so toggling back to "Yes" shows Manage Sub-Questions again
                      sub_questions_data: prev.sub_questions_data || [],
                      image_url: prev.image_url
                    };
                    if (onStateChange) {
                      onStateChange(newState);
                    }
                    return newState;
                  });
                }}
              />
            }
            label="Have sub questions?"
          />
          
          {/* Show question type, options, and correct answer only if NOT has_subquestions */}
          {!editQuestion.has_subquestions && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Question type is required for proper classification
              </Typography>

              <TextField
                select
                label="Usable in"
                value={editQuestion.exam_mode || ''}
                onChange={(e) => handleSelectChange('exam_mode', e.target.value)}
                fullWidth
              >
                <MenuItem value="">None</MenuItem>
                {examModes.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.mode}</MenuItem>
                ))}
              </TextField>
                
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Question type determines the format and validation rules
              </Typography>
              <TextField 
                select 
                label="Type *" 
                value={editQuestion.question_type} 
                onChange={(e) => handleSelectChange('question_type', e.target.value)} 
                fullWidth 
                error={!editQuestion.question_type}
              >
                {QUESTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
              
              {/* Show options only for MULTIPLE question type */}
              {editQuestion.question_type === 'MULTIPLE' ? (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Options are required for Multiple Choice questions
                  </Typography>
                  <MathEditor
                    value={editQuestion.options}
                    onChange={handleOptionsChange}
                    label="Options (comma separated) *"
                    placeholder="Enter options separated by commas, e.g. A,B,C"
                    helperText="Options are required for Multiple Choice questions. Use the math button (∑) to insert mathematical formulas."
                    minRows={2}
                    error={!(typeof editQuestion.options === 'string' && editQuestion.options.trim())}
                  />
                </>
              ) : editQuestion.question_type === 'TRUE_FALSE' ? (
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}>
                  ℹ️ Options are automatically set to ["True", "False"] for True/False questions
                </Typography>
              ) : null}
              
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Correct answer is optional. Use the math button (∑) for formulas.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {editQuestion.question_type === 'TRUE_FALSE' ? (
                  <TextField
                    select
                    label="Correct Answer"
                    value={editQuestion.correct_answer || ''}
                    onChange={(e) => handleCorrectAnswerChange(e.target.value)}
                    fullWidth={false}
                    sx={{ flex: 1 }}
                    error={editQuestion.correct_answer && !['True', 'False'].includes(editQuestion.correct_answer)}
                    helperText="Select True or False as the correct answer (optional)"
                  >
                    <MenuItem value="True">True</MenuItem>
                    <MenuItem value="False">False</MenuItem>
                  </TextField>
                ) : editQuestion.question_type === 'MULTIPLE' ? (
                  (() => {
                    const opts = parseOptions(editQuestion.options).map((o) => String(o).trim()).filter(Boolean);
                    const current = typeof editQuestion.correct_answer === 'string' ? editQuestion.correct_answer.trim() : (editQuestion.correct_answer ?? '');
                    const isNotAnOption = Boolean(current) && !opts.includes(current);
                    return (
                      <TextField
                        select
                        label="Correct Answer"
                        value={current}
                        onChange={(e) => handleCorrectAnswerChange(e.target.value)}
                        fullWidth={false}
                        sx={{ flex: 1 }}
                        disabled={opts.length === 0}
                        error={isNotAnOption}
                        helperText={
                          opts.length === 0
                            ? 'Add options above first, then select the correct answer'
                            : isNotAnOption
                              ? `"${current}" is not one of the options — select a valid option`
                              : 'Select the option that is the correct answer (optional)'
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        {isNotAnOption && (
                          <MenuItem value={current}>{current} (not in options)</MenuItem>
                        )}
                        {opts.map((opt, i) => (
                          <MenuItem key={`${opt}-${i}`} value={opt}>{opt}</MenuItem>
                        ))}
                      </TextField>
                    );
                  })()
                ) : (
                  <MathEditor
                    value={editQuestion.correct_answer}
                    onChange={handleCorrectAnswerChange}
                    label="Correct Answer"
                    placeholder="Enter the correct answer (optional)"
                    helperText="Use the math button (∑) to insert mathematical formulas."
                    minRows={1}
                    fullWidth={false}
                    sx={{ flex: 1 }}
                    error={false}
                  />
                )}
              </Box>
              <AnswerEvaluationFields
                questionType={editQuestion.question_type}
                value={editQuestion.answer_evaluation}
                onChange={handleAnswerEvaluationChange}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  onClick={() => onOpenDetailAnswer(editQuestion.id || 'new')}
                >
                  Detail Answer
                </Button>
                {(isChampions || role === 'teacher') && (() => {
                  const selectedStatus = teacherStatuses.find(s => s.id === editQuestion.status);
                  const isSubmittingForReview = selectedStatus && selectedStatus.value === 'Under Review';
                  if (detailAnswerExists) {
                    return (
                      <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                        Present
                      </Typography>
                    );
                  }
                  if (isSubmittingForReview) {
                    return (
                      <Typography variant="caption" color="warning.main">
                        ⚠ Detail Answer Required for Review
                      </Typography>
                    );
                  }
                  return null;
                })()}
              </Box>
            </>
          )}
          
          {/* Show info message and button when has_subquestions is enabled */}
          {editQuestion.has_subquestions && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontStyle: 'italic' }}>
                ℹ️ Main question does not have an answer. Each sub-question has its own answer.
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setSubQuestionDialogOpen(true)}
                  fullWidth
                >
                  {editQuestion.sub_questions_data && editQuestion.sub_questions_data.length > 0
                    ? `Manage Sub-Questions (${editQuestion.sub_questions_data.length} added)`
                    : 'Add Sub-Questions'}
                </Button>
                
                {editQuestion.sub_questions_data && editQuestion.sub_questions_data.length > 0 && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                    ✓ {editQuestion.sub_questions_data.length} sub-question{editQuestion.sub_questions_data.length > 1 ? 's' : ''} added
                  </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<InfoIcon />}
                    onClick={() => onOpenDetailAnswer(editQuestion.id || 'new')}
                    fullWidth
                  >
                    Detail Answer
                  </Button>
                  {(isChampions || role === 'teacher') && (() => {
                    const selectedStatus = teacherStatuses.find(s => s.id === editQuestion.status);
                    const isSubmittingForReview = selectedStatus && selectedStatus.value === 'Under Review';
                    if (detailAnswerExists) {
                      return (
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600, flexShrink: 0 }}>
                          Present
                        </Typography>
                      );
                    }
                    if (isSubmittingForReview) {
                      return (
                        <Typography variant="caption" color="warning.main" sx={{ flexShrink: 0 }}>
                          ⚠ Detail Answer Required for Review
                        </Typography>
                      );
                    }
                    return null;
                  })()}
                </Box>
              </Box>
            </>
          )}
          
           {/* Tags Section */}
           <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
             Tags are optional but help with organization and search
           </Typography>
           <TagSelector
             value={editQuestion.tags || []}
             onChange={handleTagsChange}
             label="Tags"
             helperText="Add descriptive tags (optional)"
           />
           
                                   {/* Status Section */}
                         {/* Check if question is published */}
            {(() => {
              // Check if current status is "Published" by looking at the status_display value
              // The backend now returns status as ID and status_display as the human-readable value
              const isPublished = editQuestion.status_display === 'Published';
              
              // Update help text based on published status
              const helpText = isPublished 
                ? "⚠️ Others have started using it. It can not be taken back. Thank you!"
                : "Do you want to share the question with others and get rewarded? Please remember once published, it can not be taken back.";
              
              return (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    {helpText}
                  </Typography>
                 
                                   <TextField
                    select
                    label="Publish to others"
                    value={editQuestion.status || ''}
                    onChange={(e) => handleSelectChange('status', e.target.value)}
                    fullWidth
                    size="small"
                    disabled={isPublished}
                    error={!!detailAnswerError}
                    helperText={detailAnswerError || (() => {
                      // Show helpful message when Submit for Review is selected
                      const selectedStatus = teacherStatuses.find(s => s.id === editQuestion.status);
                      if (selectedStatus && selectedStatus.value === 'Under Review' && !detailAnswerExists) {
                        return "Detail answer is required for review submission";
                      }
                      return "";
                    })()}
                  >
                   <MenuItem value="">No Status</MenuItem>
                   
                   {/* For teachers and champions, show only Draft and Submit for Review */}
                   {(isChampions || role === 'teacher') ? (
                     teacherStatuses.length > 0 ? (
                       teacherStatuses
                         .filter(status => status.value === 'Draft' || status.value === 'Under Review')
                         .map((statusItem) => (
                           <MenuItem key={statusItem.id} value={statusItem.id}>
                             {statusItem.value === 'Under Review' ? 'Submit for Review' : statusItem.value}
                           </MenuItem>
                         ))
                     ) : (
                       <MenuItem value="" disabled>
                         Loading statuses...
                       </MenuItem>
                     )
                   ) : (
                     /* For admin users, show all statuses */
                     statuses.length > 0 ? (
                       statuses.map((statusItem) => (
                         <MenuItem key={statusItem.id} value={statusItem.id}>
                           {statusItem.value}
                         </MenuItem>
                       ))
                     ) : (
                       <MenuItem value="" disabled>
                         Loading statuses...
                       </MenuItem>
                     )
                   )}
                 </TextField>

                 {/* Detail Answer Validation Alert */}
                 {detailAnswerError && (
                   <Alert severity="error" sx={{ mt: 1 }}>
                     {detailAnswerError}
                   </Alert>
                 )}
               </>
             );
           })()}
           
                       {/* Active Toggle Section */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Active status controls whether the question is available for use in tests
            </Typography>
           <FormControlLabel 
             control={
               <Switch 
                 checked={editQuestion.is_active} 
                 onChange={(e) => handleBooleanChange('is_active', e.target.checked)} 
               />
             } 
             label="Active" 
           />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={
            status.hasErrors || 
            loading || 
            (editQuestion.has_subquestions && (!editQuestion.sub_questions_data || editQuestion.sub_questions_data.length === 0))
          }
        >
          {loading ? 'Saving...' : (mode === 'create' ? 'Add Question' : 'Update Question')}
        </Button>
      </DialogActions>
      
      {/* Sub-Question Dialog */}
      <SubQuestionDialog
        open={subQuestionDialogOpen}
        onClose={() => setSubQuestionDialogOpen(false)}
        subQuestions={editQuestion.sub_questions_data || []}
        onSave={(updatedSubQuestions, imageFilesByIndex = {}, detailImageFilesByIndex = {}) => {
          const files = imageFilesByIndex && typeof imageFilesByIndex === 'object' ? imageFilesByIndex : {};
          // Merge so we never lose files from previous callbacks (e.g. handleImageChange then Save & Close)
          subQuestionImageFilesRef.current = { ...subQuestionImageFilesRef.current, ...files };
          const detailFiles = detailImageFilesByIndex && typeof detailImageFilesByIndex === 'object' ? detailImageFilesByIndex : {};
          subQuestionDetailImageFilesRef.current = { ...subQuestionDetailImageFilesRef.current, ...detailFiles };
          setEditQuestion(prev => {
            const newState = {
              ...prev,
              sub_questions_data: updatedSubQuestions.map((sq, idx) => ({
                ...sq,
                order: idx + 1
              }))
            };
            if (onStateChange) {
              onStateChange(newState);
            }
            return newState;
          });
          setSubQuestionImageFiles(subQuestionImageFilesRef.current);
        }}
      />
    </Dialog>
  );
};

export default QuestionDialog;
